import type { Rng } from '../rng.js'
import {
  levelOf,
  MAX_LEVEL,
  MIN_LEVEL,
  overallLevel,
  SKILLS,
  type Question,
  type Skill,
  type SkillLevels,
} from './maths-skills.js'

/** A correct answer inside this counts as fluent rather than worked out. */
export const FLUENT_MS = 5_000

export type AnswerSpeed = 'fluent' | 'considered'

/**
 * How a skill's level moves after an answer.
 *
 * Three outcomes rather than two. A correct answer that took a while means the
 * fact is not yet automatic, so it holds rather than advancing — otherwise
 * somebody who can work everything out slowly is pushed into material they
 * cannot reach, which is how a game stops being fun.
 *
 * Wrong drops one, never further. A single slip should not undo a fortnight.
 */
export function nextLevel(current: number, correct: boolean, speed: AnswerSpeed): number {
  if (!correct) return Math.max(MIN_LEVEL, current - 1)
  if (speed === 'considered') return current
  return Math.min(MAX_LEVEL, current + 1)
}

export function applyAnswer(
  levels: SkillLevels,
  skillKey: string,
  correct: boolean,
  elapsedMs: number,
): SkillLevels {
  const speed: AnswerSpeed = elapsedMs <= FLUENT_MS ? 'fluent' : 'considered'

  return { ...levels, [skillKey]: nextLevel(levelOf(levels, skillKey), correct, speed) }
}

/**
 * Which skills somebody has reached.
 *
 * There is always at least one: the easiest skills unlock at level zero and
 * an overall level can never fall below it, so a brand-new player always has
 * something to answer without needing a fallback.
 */
export function availableSkills(levels: SkillLevels): readonly Skill[] {
  const reached = overallLevel(levels)
  return SKILLS.filter((skill) => skill.unlocksAt <= reached)
}

/**
 * The skills that opened up most recently.
 *
 * This is the only real sense of progression left now that there are no
 * scores, and it is the good kind: harder questions arrive because the easier
 * ones stopped being hard. Nothing is awarded and nothing is compared — the
 * questions simply change, and this is what lets the app say so out loud
 * rather than leaving her to notice.
 *
 * The newest tier rather than every unlocked skill, because "you are onto the
 * seven times table now" is news and a list of twenty things she can already
 * do is wallpaper.
 */
export function newestSkills(levels: SkillLevels): readonly Skill[] {
  const reached = availableSkills(levels)

  // Everything unlocks at level zero for a new player, and "you have unlocked
  // adding and taking away" is not news on the first go.
  const newest = reached.reduce((highest, skill) => Math.max(highest, skill.unlocksAt), 0)
  if (newest === 0) return []

  return reached.filter((skill) => skill.unlocksAt === newest)
}

/**
 * What is coming next, so there is something to be curious about.
 *
 * Deliberately not a target with a number attached. She cannot see the level
 * and cannot work out how far off it is; all this says is that there is more,
 * which is true and is the whole of what a child needs to hear.
 */
export function nextSkills(levels: SkillLevels): readonly Skill[] {
  const reached = overallLevel(levels)

  const soonest = SKILLS.filter((skill) => skill.unlocksAt > reached).reduce(
    (lowest: number | null, skill) =>
      lowest === null ? skill.unlocksAt : Math.min(lowest, skill.unlocksAt),
    null,
  )

  if (soonest === null) return []

  return SKILLS.filter((skill) => skill.unlocksAt === soonest)
}

/**
 * How often a skill should come up.
 *
 * Weak skills are weighted up, but every unlocked skill keeps a floor so a
 * session is a mix rather than a drill. Being handed nothing but your worst
 * subject is what makes practice feel like a punishment, and she would stop
 * opening the app.
 */
export function weightOf(levels: SkillLevels, skill: Skill): number {
  const level = levelOf(levels, skill.key)
  return 1 + (MAX_LEVEL - level)
}

/**
 * Picks from a given set, favouring the weak ones without excluding the strong.
 *
 * Takes the candidates rather than deriving them, so a caller dealing a whole
 * round can fix what is available once and let only the weighting move.
 */
export function chooseFrom(skills: readonly Skill[], levels: SkillLevels, rng: Rng): Skill {
  const weights = skills.map((skill) => weightOf(levels, skill))
  const total = weights.reduce((sum, weight) => sum + weight, 0)

  let ticket = rng.int(total)
  for (const [index, weight] of weights.entries()) {
    ticket -= weight
    if (ticket < 0) return skills[index] as Skill
  }

  return skills[skills.length - 1] as Skill
}

/** Picks from everything the player has reached. */
export function chooseSkill(levels: SkillLevels, rng: Rng): Skill {
  return chooseFrom(availableSkills(levels), levels, rng)
}

export function buildQuestion(levels: SkillLevels, rng: Rng): Question {
  return chooseSkill(levels, rng).build(rng)
}
